import { parseSurveyTeamScheduledSlots } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import { buildQuotationGroupIndexMap, groupLabelForTeamRow } from './scheduleGroupIndex';
import { dynamicRelation, memberKeyFromTeamRow, userLabelFromTeamRow, workOrderDocIdFromTeamRow } from './utils';

export type QuotationCalendarDayEntry = {
  quotationDocumentId: string;
  quotationNumber: string;
  scheduleDocumentId: string;
  scheduleNumber: string;
  slotCount: number;
};

export type QuotationScheduleDayMember = {
  userId: string;
  name: string;
  isLead: boolean;
  timeLabels: string[];
};

export type QuotationScheduleDaySummary = {
  quotationDocumentId: string;
  quotationNumber: string;
  scheduleDocumentId: string;
  scheduleNumber: string;
  scheduleStatus: string;
  customerName: string;
  address: string;
  notes: string;
  /** Catatan transportasi / kendaraan pada penjadwalan. */
  transportasiNote: string;
  measurementTypesLabel: string;
  samplingReceivedAt: string;
  teamLeadName: string;
  teamCount: number;
  members: QuotationScheduleDayMember[];
  /** Nama tim bebas (dari `survey_teams.team_name`); kosong = pakai Tim N otomatis. */
  teamName: string | null;
  /** Quotation kontrak/konsultan — nama group sample pada penjadwalan ini. */
  groupLabel: string | null;
  /** Quotation kontrak. Card menampilkan group dan index recurring hanya untuk ini. */
  isContract?: boolean;
  /** `quotation_recurring_events.event_index` pada jadwal kontrak. */
  recurringEventIndex?: number | null;
  workOrderDocumentId: string;
  /** Nomor job GL/GLJ/GLM; kosong sampai admin sampling menyetujui tim. */
  workOrderJobNumber?: string;
  /** Semua titik berita acara work order ini sudah TTD lengkap (terbit). */
  beritaAcaraAllIssued: boolean;
};

export type QuotationCalendarDayState = 'empty' | 'current' | 'other';

/** Kartu biru di kalender detail. Tanpa quotation id: hanya jadwal yang sedang dibuka. */
export function isActiveScheduleOnDetailCalendar(
  summary: Pick<QuotationScheduleDaySummary, 'scheduleDocumentId' | 'quotationDocumentId'>,
  currentScheduleDocumentId: string,
  activeQuotationDocumentId?: string | null,
): boolean {
  const quotationId = String(activeQuotationDocumentId ?? '').trim();
  if (quotationId) {
    return String(summary.quotationDocumentId ?? '').trim() === quotationId;
  }
  const scheduleId = String(currentScheduleDocumentId ?? '').trim();
  return Boolean(scheduleId) && String(summary.scheduleDocumentId ?? '').trim() === scheduleId;
}

/** Kartu abu: semua titik berita acara pada work order sudah terbit (TTD lengkap). */
export function isScheduleCardCompleted(
  summary: Pick<QuotationScheduleDaySummary, 'beritaAcaraAllIssued'>,
): boolean {
  return Boolean(summary.beritaAcaraAllIssued);
}

export function applyBeritaAcaraIssuedFlag(
  summary: QuotationScheduleDaySummary,
  issuedWorkOrderIds?: Set<string>,
): QuotationScheduleDaySummary {
  const workOrderId = String(summary.workOrderDocumentId ?? '').trim();
  const issued = Boolean(workOrderId && issuedWorkOrderIds?.has(workOrderId));
  if (summary.beritaAcaraAllIssued === issued) return summary;
  return { ...summary, beritaAcaraAllIssued: issued };
}

function customerAddressFromRelation(customer: Record<string, unknown> | null): string {
  if (!customer) return '';
  const parts = [
    String(customer.address ?? '').trim(),
    String(customer.city ?? '').trim(),
    String(customer.province ?? '').trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

function scheduleRelationFromTeam(team: DynamicEntityRow): DynamicEntityRow | null {
  const fromSchedule = dynamicRelation(team, 'sampling_schedule');
  if (fromSchedule) return fromSchedule as unknown as DynamicEntityRow;
  const workOrder = dynamicRelation(team, 'work_order');
  if (!workOrder) return null;
  const many = workOrder.sampling_schedules;
  if (Array.isArray(many) && many.length > 0) {
    const rows = many.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object');
    const initial = rows.find((r) => String(r.schedule_kind ?? 'initial') === 'initial');
    const picked = initial ?? rows[0];
    return picked ? (picked as unknown as DynamicEntityRow) : null;
  }
  const schedule = dynamicRelation(workOrder as unknown as DynamicEntityRow, 'sampling_schedule');
  return schedule ? (schedule as unknown as DynamicEntityRow) : null;
}

function scheduleMeasurementTypesLabel(schedule: Record<string, unknown> | null): string {
  if (!schedule) return '';
  const plansRaw = schedule.sampling_plans;
  if (!Array.isArray(plansRaw) || plansRaw.length === 0) return '';
  const labels = new Set<string>();
  plansRaw.forEach((plan) => {
    if (!plan || typeof plan !== 'object') return;
    const line = dynamicRelation(plan as DynamicEntityRow, 'quotation_sample_information');
    const jenis = line ? dynamicRelation(line as DynamicEntityRow, 'jenis_pengukuran') : null;
    const nama = String(jenis?.nama_jenis ?? '').trim();
    const kode = String(jenis?.kode_jenis ?? '').trim();
    const label = nama || kode;
    if (label) labels.add(label);
  });
  return [...labels].sort((a, b) => a.localeCompare(b, 'id')).join(', ');
}

export function scheduleMetaFromTeamRow(team: DynamicEntityRow): {
  scheduleDocumentId: string;
  scheduleNumber: string;
  scheduleStatus: string;
  quotationDocumentId: string;
  quotationNumber: string;
  customerName: string;
  address: string;
  notes: string;
  transportasiNote: string;
  measurementTypesLabel: string;
  samplingReceivedAt: string;
  workOrderDocumentId: string;
  workOrderJobNumber: string;
  isContract: boolean;
  recurringEventIndex: number | null;
} | null {
  const schedule = scheduleRelationFromTeam(team);
  const meta = quotationFromScheduleRelation(schedule);
  if (!meta?.scheduleDocumentId) return null;
  const q = schedule ? dynamicRelation(schedule as DynamicEntityRow, 'quotation') : null;
  const group = schedule ? dynamicRelation(schedule as DynamicEntityRow, 'quotation_group') : null;
  const groupCustomer = group ? dynamicRelation(group as DynamicEntityRow, 'customer') : null;
  const customer = q ? dynamicRelation(q as DynamicEntityRow, 'customer') : null;
  const customerName = String(groupCustomer?.name ?? customer?.name ?? '').trim();
  const address = customerAddressFromRelation(groupCustomer ?? customer);
  const notes = String(schedule?.notes ?? '').trim();
  const transportasiNote = String(schedule?.transportasi_note ?? '').trim();
  const measurementFromField = String(schedule?.measurement_types_label ?? '').trim();
  const measurementTypesLabel =
    measurementFromField || scheduleMeasurementTypesLabel(schedule as Record<string, unknown> | null);
  const samplingReceivedAt = String(schedule?.sampling_received_at ?? '').trim();
  const scheduleStatus = String(schedule?.progress_status ?? '').trim();
  const workOrderFromSchedule = schedule
    ? String(dynamicRelation(schedule as DynamicEntityRow, 'work_order')?.documentId ?? '').trim()
    : '';
  const workOrderDocumentId = workOrderFromSchedule || workOrderDocIdFromTeamRow(team);
  const workOrderJobNumber =
    String(
      (schedule
        ? dynamicRelation(schedule as DynamicEntityRow, 'work_order')?.job_number
        : '') ?? '',
    ).trim() ||
    String(dynamicRelation(team, 'work_order')?.job_number ?? '').trim();
  const recurringEvent = schedule
    ? dynamicRelation(schedule as DynamicEntityRow, 'quotation_recurring_event')
    : null;
  const recurringEventIndexRaw = Number(recurringEvent?.event_index);
  return {
    scheduleDocumentId: meta.scheduleDocumentId,
    scheduleNumber: meta.scheduleNumber,
    quotationDocumentId: meta.documentId,
    quotationNumber: meta.quotationNumber,
    customerName,
    address,
    notes,
    transportasiNote,
    measurementTypesLabel,
    samplingReceivedAt,
    scheduleStatus,
    workOrderDocumentId,
    workOrderJobNumber,
    isContract: Boolean(q?.is_contract),
    recurringEventIndex: Number.isFinite(recurringEventIndexRaw) ? recurringEventIndexRaw : null,
  };
}

export function quotationFromScheduleRelation(
  schedule: Record<string, unknown> | null,
): { documentId: string; quotationNumber: string; scheduleDocumentId: string; scheduleNumber: string } | null {
  if (!schedule) return null;
  const scheduleDocumentId = String(schedule.documentId ?? '').trim();
  if (!scheduleDocumentId) return null;
  const q = dynamicRelation(schedule as DynamicEntityRow, 'quotation');
  const quotationDocumentId = String(q?.documentId ?? '').trim();
  const quotationNumber = String(q?.quotation_number ?? '').trim() || quotationDocumentId || 'Quotation';
  const scheduleNumber = String(schedule.schedule_number ?? '').trim();
  return { documentId: quotationDocumentId, quotationNumber, scheduleDocumentId, scheduleNumber };
}

/** Map tanggal → entri quotation (dari slot tim survey). */
export function buildQuotationCalendarByDate(
  teams: DynamicEntityRow[],
): Map<string, QuotationCalendarDayEntry[]> {
  const byDate = new Map<string, Map<string, QuotationCalendarDayEntry>>();

  for (const team of teams) {
    const schedule = dynamicRelation(team, 'sampling_schedule');
    const meta = quotationFromScheduleRelation(schedule);
    if (!meta?.scheduleDocumentId) continue;

    const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);
    for (const slot of slots) {
      const date = slot.date;
      if (!byDate.has(date)) byDate.set(date, new Map());
      const dayMap = byDate.get(date)!;
      const key = meta.scheduleDocumentId;
      const existing = dayMap.get(key);
      if (existing) {
        existing.slotCount += 1;
      } else {
        dayMap.set(key, {
          quotationDocumentId: meta.documentId,
          quotationNumber: meta.quotationNumber,
          scheduleDocumentId: meta.scheduleDocumentId,
          scheduleNumber: meta.scheduleNumber,
          slotCount: 1,
        });
      }
    }
  }

  const out = new Map<string, QuotationCalendarDayEntry[]>();
  for (const [date, dayMap] of byDate.entries()) {
    out.set(
      date,
      [...dayMap.values()].sort((a, b) => a.quotationNumber.localeCompare(b.quotationNumber, 'id')),
    );
  }
  return out;
}

export function quotationCalendarDayState(
  date: string,
  byDate: Map<string, QuotationCalendarDayEntry[]>,
  currentQuotationDocumentId?: string | null,
): QuotationCalendarDayState {
  const entries = byDate.get(date) ?? [];
  if (!entries.length) return 'empty';
  const currentId = currentQuotationDocumentId?.trim() ?? '';
  if (!currentId) return 'current';
  if (entries.some((e) => e.quotationDocumentId === currentId)) return 'current';
  return 'other';
}

export function currentQuotationEntriesOnDate(
  date: string,
  byDate: Map<string, QuotationCalendarDayEntry[]>,
  currentQuotationDocumentId?: string | null,
): QuotationCalendarDayEntry[] {
  const currentId = currentQuotationDocumentId?.trim() ?? '';
  if (!currentId) return [];
  return (byDate.get(date) ?? []).filter((e) => e.quotationDocumentId === currentId);
}

export function otherQuotationEntriesOnDate(
  date: string,
  byDate: Map<string, QuotationCalendarDayEntry[]>,
  currentQuotationDocumentId?: string | null,
): QuotationCalendarDayEntry[] {
  const currentId = currentQuotationDocumentId?.trim() ?? '';
  return (byDate.get(date) ?? []).filter((e) => !currentId || e.quotationDocumentId !== currentId);
}

type DaySummaryBucket = {
  meta: ReturnType<typeof scheduleMetaFromTeamRow>;
  groupLabel: string | null;
  teamName: string | null;
  members: Map<string, QuotationScheduleDayMember>;
};

/** Ringkasan quotation terjadwal per tanggal (dari baris tim survey). */
export function buildQuotationScheduleDaySummariesByDate(
  teams: DynamicEntityRow[],
): Map<string, QuotationScheduleDaySummary[]> {
  const groupIndexByQuotation = buildQuotationGroupIndexMap(teams);
  const byDate = new Map<string, Map<string, DaySummaryBucket>>();

  for (const team of teams) {
    const meta = scheduleMetaFromTeamRow(team);
    if (!meta) continue;
    const userId = memberKeyFromTeamRow(team);
    if (!userId) continue;
    const memberName = userLabelFromTeamRow(team);
    const isLead = Boolean(team.is_lead);
    const teamName = String(team.team_name ?? '').trim() || null;
    const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);

    for (const slot of slots) {
      const date = slot.date;
      if (!byDate.has(date)) byDate.set(date, new Map());
      const dayMap = byDate.get(date)!;
      let bucket = dayMap.get(meta.scheduleDocumentId);
      if (!bucket) {
        bucket = {
          meta,
          groupLabel: groupLabelForTeamRow(team, groupIndexByQuotation),
          teamName,
          members: new Map(),
        };
        dayMap.set(meta.scheduleDocumentId, bucket);
      } else {
        if (
          (!bucket.meta?.workOrderDocumentId && meta.workOrderDocumentId) ||
          (!bucket.meta?.workOrderJobNumber && meta.workOrderJobNumber)
        ) {
          bucket.meta = { ...bucket.meta, ...meta };
        }
        if (!bucket.teamName && teamName) bucket.teamName = teamName;
      }
      const existing = bucket.members.get(userId);
      const timeLabel = slot.timeLabel || 'Sepanjang hari';
      if (existing) {
        if (!existing.timeLabels.includes(timeLabel)) existing.timeLabels.push(timeLabel);
        existing.isLead = existing.isLead || isLead;
      } else {
        bucket.members.set(userId, { userId, name: memberName, isLead, timeLabels: [timeLabel] });
      }
    }
  }

  const out = new Map<string, QuotationScheduleDaySummary[]>();
  for (const [date, dayMap] of byDate.entries()) {
    const summaries: QuotationScheduleDaySummary[] = [...dayMap.values()]
      .filter((b) => b.meta)
      .map((bucket) => {
        const members = [...bucket.members.values()].sort((a, b) => {
          if (a.isLead !== b.isLead) return a.isLead ? -1 : 1;
          return a.name.localeCompare(b.name, 'id');
        });
        const lead = members.find((m) => m.isLead) ?? members[0];
        return {
          quotationDocumentId: bucket.meta!.quotationDocumentId,
          quotationNumber: bucket.meta!.quotationNumber,
          scheduleDocumentId: bucket.meta!.scheduleDocumentId,
          scheduleNumber: bucket.meta!.scheduleNumber,
          scheduleStatus: bucket.meta!.scheduleStatus,
          customerName: bucket.meta!.customerName,
          address: bucket.meta!.address,
          notes: bucket.meta!.notes,
          transportasiNote: bucket.meta!.transportasiNote,
          measurementTypesLabel: bucket.meta!.measurementTypesLabel,
          samplingReceivedAt: bucket.meta!.samplingReceivedAt,
          workOrderDocumentId: bucket.meta!.workOrderDocumentId,
          workOrderJobNumber: bucket.meta!.workOrderJobNumber,
          beritaAcaraAllIssued: false,
          teamLeadName: lead?.name ?? '—',
          teamCount: members.length,
          members,
          teamName: bucket.teamName,
          groupLabel: bucket.groupLabel,
          isContract: bucket.meta!.isContract,
          recurringEventIndex: bucket.meta!.recurringEventIndex,
        };
      })
      .sort((a, b) => a.quotationNumber.localeCompare(b.quotationNumber, 'id'));
    out.set(date, summaries);
  }
  return out;
}

export type TeamMemberAvailability = {
  userId: string;
  name: string;
  isLead: boolean;
  available: boolean;
  busyQuotationNumber?: string;
  busyTimeLabels: string[];
  assignedOnDate: boolean;
};

/** Ketersediaan anggota tim pada tanggal (lintas penjadwalan). */
export function surveyTeamAvailabilityOnDate(
  date: string,
  memberUserIds: string[],
  memberLabels: Map<string, { name: string; isLead: boolean }>,
  allTeams: DynamicEntityRow[],
  currentScheduleDocumentId: string,
): TeamMemberAvailability[] {
  return memberUserIds.map((userId) => {
    const label = memberLabels.get(userId);
    let assignedOnDate = false;
    let busyQuotationNumber: string | undefined;
    const busyTimeLabelsSet = new Set<string>();

    for (const team of allTeams) {
      const userRec = dynamicRelation(team, 'user_idx');
      const teamUserId = String(userRec?.documentId ?? userRec?.id ?? '').trim();
      if (teamUserId !== userId) continue;
      const dateSlots = parseSurveyTeamScheduledSlots(team.scheduled_date).filter((s) => s.date === date);
      if (!dateSlots.length) continue;

      const scheduleRel = dynamicRelation(team, 'sampling_schedule');
      const scheduleDocId = String(scheduleRel?.documentId ?? '').trim();
      if (scheduleDocId === currentScheduleDocumentId) {
        assignedOnDate = true;
        continue;
      }
      const meta = scheduleMetaFromTeamRow(team);
      busyQuotationNumber = meta?.quotationNumber ?? 'Quotation lain';
      dateSlots.forEach((slot) => busyTimeLabelsSet.add(slot.timeLabel || 'Sepanjang hari'));
    }

    const busyTimeLabels = [...busyTimeLabelsSet].sort();
    const allDayBusy = busyTimeLabels.includes('Sepanjang hari');
    return {
      userId,
      name: label?.name ?? userId,
      isLead: label?.isLead ?? false,
      available: !allDayBusy,
      busyQuotationNumber,
      busyTimeLabels,
      assignedOnDate,
    };
  });
}

function teamRowMatchesUser(
  team: DynamicEntityRow,
  user: { id: string; documentId?: string; numericId?: number | null },
): boolean {
  const freelanceRec = dynamicRelation(team, 'freelance_user');
  const freelanceDocId = String(freelanceRec?.documentId ?? '').trim();
  if (freelanceDocId) {
    if (user.id === `freelance:${freelanceDocId}`) return true;
    if (user.documentId && user.documentId === freelanceDocId) return true;
    return false;
  }

  const userRec = dynamicRelation(team, 'user_idx');
  if (!userRec) return false;
  const docId = String(userRec.documentId ?? '').trim();
  const numericId = Number(userRec.id);
  if (user.documentId && docId && user.documentId === docId) return true;
  if (user.numericId != null && Number.isFinite(numericId) && user.numericId === numericId) return true;
  if (docId && docId === user.id) return true;
  if (Number.isFinite(numericId) && String(numericId) === user.id) return true;
  return false;
}

/** Ketersediaan semua petugas survey pada tanggal (untuk modal tambah slot). */
export function surveyTeamAvailabilityForUsers(
  date: string,
  users: Array<{ id: string; documentId?: string; numericId?: number | null; name: string; isLead?: boolean }>,
  allTeams: DynamicEntityRow[],
  currentScheduleDocumentId: string,
): TeamMemberAvailability[] {
  return users
    .map((user) => {
      let assignedOnDate = false;
      let busyQuotationNumber: string | undefined;
      const busyTimeLabelsSet = new Set<string>();

      for (const team of allTeams) {
        if (!teamRowMatchesUser(team, user)) continue;
        const dateSlots = parseSurveyTeamScheduledSlots(team.scheduled_date).filter((s) => s.date === date);
        if (!dateSlots.length) continue;

        const scheduleRel = dynamicRelation(team, 'sampling_schedule');
        const scheduleDocId = String(scheduleRel?.documentId ?? '').trim();
        if (scheduleDocId === currentScheduleDocumentId) {
          assignedOnDate = true;
          continue;
        }
        const meta = scheduleMetaFromTeamRow(team);
        busyQuotationNumber = meta?.quotationNumber ?? 'Quotation lain';
        dateSlots.forEach((slot) => busyTimeLabelsSet.add(slot.timeLabel || 'Sepanjang hari'));
      }

      const busyTimeLabels = [...busyTimeLabelsSet].sort();
      const allDayBusy = busyTimeLabels.includes('Sepanjang hari');
      return {
        userId: user.id,
        name: user.name,
        isLead: user.isLead ?? false,
        available: !allDayBusy,
        busyQuotationNumber,
        busyTimeLabels,
        assignedOnDate,
      };
    })
    .sort((a, b) => {
      if (a.available !== b.available) return a.available ? -1 : 1;
      if (a.assignedOnDate !== b.assignedOnDate) return a.assignedOnDate ? -1 : 1;
      return a.name.localeCompare(b.name, 'id');
    });
}
